import React, { FC } from 'react';
import { Button, Empty, Modal } from 'antd';
import { ComplianceChecklistReview } from './ComplianceChecklistReview';
import { ComplianceDocumentSplitView } from './ComplianceDocumentSplitView';
import {
	COMPLIANCE_SPLIT_MODAL_BODY_STYLE,
	COMPLIANCE_SPLIT_MODAL_TOP,
	COMPLIANCE_SPLIT_MODAL_WIDTH,
	COMPLIANCE_SPLIT_PANE_HEIGHT,
} from './ComplianceConstants';
import { ComplianceRequest } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

interface ComplianceChecklistModalProps {
	visible: boolean;
	// The request whose stored checklistExtraction is shown. Read straight off the
	// request — the extraction is persisted with it, so there is nothing to fetch.
	complianceRequest: ComplianceRequest;
	onCancel: () => void;
}

// Read-only view of the checklist that was extracted from a request's sample
// document when the request was created. Same rendering the create wizard's
// review step uses.
export const ComplianceChecklistModal: FC<ComplianceChecklistModalProps> = ({
	visible,
	complianceRequest,
	onCancel,
}): React.ReactElement => {
	const extraction = nullSafeGet('checklistExtraction', complianceRequest);
	const hasItems = nullSafeGetOrElse('items.length', extraction, 0) > 0;
	const sampleFileUrl = nullSafeGet('sampleFileUrl', complianceRequest);
	const sampleFileName = nullSafeGetOrElse('name', complianceRequest, 'Sample document');
	const footer = (
		<Button type="primary" onClick={onCancel}>
			DONE
		</Button>
	);
	return (
		<Modal
			visible={visible}
			title={`Checklist: ${nullSafeGetOrElse('name', complianceRequest, 'Compliance request')}`}
			onCancel={onCancel}
			footer={footer}
			width={COMPLIANCE_SPLIT_MODAL_WIDTH}
			style={{ top: COMPLIANCE_SPLIT_MODAL_TOP }}
			bodyStyle={COMPLIANCE_SPLIT_MODAL_BODY_STYLE}
		>
			{/* The requirements never stand alone: they are shown beside the sample they
			    were read from, so a reader can check one against the other. */}
			<ComplianceDocumentSplitView
				documentUrl={sampleFileUrl}
				fileName={sampleFileName}
				height={COMPLIANCE_SPLIT_PANE_HEIGHT}
			>
				{hasItems ? (
					<ComplianceChecklistReview extraction={extraction} />
				) : (
					<Empty description="No checklist was read from this request's sample document." />
				)}
			</ComplianceDocumentSplitView>
		</Modal>
	);
};
